import React from "react";
import PhoneUpButton from "../PhoneUpButton";

const ConversationSkeletonLoader: React.FC = () => {
  return (
    <div className="w-full h-full flex flex-col bg-white relative gap-0 rounded-lg overflow-hidden">
      {/* Chat Header Skeleton. The back affordance is REAL (#11742 round
          2.2): up-to-the-starter works during load, matching the bar the
          skeleton stands in for. */}
      <div className="absolute top-0 left-0 right-0 z-10">
        <div className="bg-white px-4 py-3 flex justify-between">
          {/* Chat Title */}
          <div className="flex items-center gap-2 text-gray-800 font-semibold text-lg">
            <PhoneUpButton to="/chats" label="Back to chats" />
            <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Right side - Share button and Profile Icon */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="w-[1px] h-6 bg-gray-200"></div>
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Messages Area Skeleton */}
      <div
        className="overflow-auto p-4 pt-16 pb-24"
        style={{
          position: "absolute",
          top: "56px", // Height of header
          bottom: "170px", // Space for floating input
          left: 0,
          right: 0,
        }}
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {/* User Message Skeleton */}
          <div className="w-full">
            <div className="flex items-start gap-3 w-full">
              {/* User Icon */}
              <div className="flex-shrink-0 mt-4">
                <div className="w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
              </div>

              {/* User Message Content */}
              <div className="flex-1 rounded-xl p-4 text-gray-800 max-w-full bg-gray-100">
                <div className="min-h-[20px]">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-300 rounded w-64 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded w-48 animate-pulse"></div>
                  </div>
                </div>

                {/* File attachments skeleton */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="w-16 h-4 bg-blue-200 rounded animate-pulse"></div>
                    </div>
                    <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="w-20 h-4 bg-blue-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bot Message Skeleton - with thinking */}
          <div className="w-full">
            <div className="flex flex-col gap-0 w-full">
              {/* Bot Icon and Thinking Status */}
              <div className="flex items-center gap-3 w-full mb-2">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <div className="w-7 h-7 bg-blue-300 rounded animate-pulse"></div>
                  </div>
                </div>
                {/* Thinking status text */}
                <div className="flex items-center text-[#2A3DCB]">
                  <div className="w-20 h-4 bg-blue-200 rounded animate-pulse"></div>
                </div>
                {/* Dropdown arrow */}
                <div className="flex items-center gap-2 cursor-pointer text-[#2A3DCB]">
                  <div className="w-4 h-4 bg-blue-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* Thinking Display Skeleton */}
              <div className="px-3 py-2 rounded-lg">
                <div className="flex p-2 gap-4 ml-6">
                  {/* Grey line */}
                  <div className="flex w-[4px] bg-gray-200 rounded-full self-stretch" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-500">
                      <div className="space-y-1">
                        <div className="h-3 bg-gray-300 rounded w-full animate-pulse"></div>
                        <div className="h-3 bg-gray-300 rounded w-3/4 animate-pulse"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bot Message Content */}
              <div className="flex-1 text-gray-800 max-w-full bg-white border border-gray-200">
                <div className="min-h-[20px] p-4">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
                  </div>
                </div>

                {/* File attachments skeleton */}
                <div className="pt-3 px-4 pb-4 border-t border-gray-200">
                  <div className="text-xs text-gray-600 mb-2">
                    <div className="w-20 h-3 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="w-16 h-4 bg-blue-200 rounded animate-pulse"></div>
                    </div>
                    <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="w-20 h-4 bg-blue-200 rounded animate-pulse"></div>
                    </div>
                    <div className="inline-flex items-center px-2 py-1 rounded-lg bg-gray-50">
                      <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Action buttons skeleton */}
                <div className="flex items-center gap-1 mt-2 pt-2 px-4 pb-4">
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600">
                    <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-8 h-3 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Another User Message Skeleton */}
          <div className="w-full">
            <div className="flex items-start gap-3 w-full">
              {/* User Icon */}
              <div className="flex-shrink-0 mt-4">
                <div className="w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
              </div>

              {/* User Message Content */}
              <div className="flex-1 rounded-xl p-4 text-gray-800 max-w-full bg-gray-100">
                <div className="min-h-[20px]">
                  <div className="h-4 bg-gray-300 rounded w-48 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Another Bot Message Skeleton - completed */}
          <div className="w-full">
            <div className="flex flex-col gap-0 w-full">
              {/* Bot Icon and Completed Status */}
              <div className="flex items-center gap-3 w-full mb-2">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <div className="w-7 h-7 bg-blue-300 rounded animate-pulse"></div>
                  </div>
                </div>
                {/* Completed status text */}
                <div className="flex items-center text-[#2A3DCB]">
                  <div className="w-24 h-4 bg-blue-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* Bot Message Content */}
              <div className="flex-1 text-gray-800 max-w-full bg-white border border-gray-200">
                <div className="min-h-[20px] p-4">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  </div>
                </div>

                {/* Chart loading skeleton */}
                <div className="my-6 px-4">
                  <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
                      <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Action buttons skeleton */}
                <div className="flex items-center gap-1 mt-2 pt-2 px-4 pb-4">
                  <div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600">
                    <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-8 h-3 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating ChatInputBar bubble skeleton at bottom. z-20, page chrome:
          the nav's hover panel (z-30, Layout) paints over it (#12209). */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pb-4 bg-white">
        <div className="max-w-3xl mx-auto w-full">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 w-full">
            <div className="flex items-end gap-2">
              {/* File selector button skeleton */}
              <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse"></div>

              {/* Tools button skeleton */}
              <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse"></div>

              {/* Textarea skeleton */}
              <div className="flex-1">
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
              </div>

              {/* Send button skeleton */}
              <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationSkeletonLoader;
